import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { isMobile, isStandalonePWA } from "@/lib/platform";
import { MessageSquare, User, LogOut, Radio, Shield, Users as UsersIcon, AtSign } from "lucide-react";

export default function TopBar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [unread, setUnread] = useState(0);
  const [mentionsUnread, setMentionsUnread] = useState(0);
  const [online, setOnline] = useState(null);

  useEffect(() => {
    const onUpdate = (e) => setUnread(e?.detail?.count || 0);
    window.addEventListener("nw:unread-dms", onUpdate);
    return () => window.removeEventListener("nw:unread-dms", onUpdate);
  }, []);

  useEffect(() => {
    // Mentions unread count arrives via useUnreadDMs polling every 8s and via
    // a manual dispatch when the user opens /mentions (which clears server-side).
    const onMentions = (e) => setMentionsUnread(e?.detail?.count || 0);
    window.addEventListener("nw:unread-mentions", onMentions);
    return () => window.removeEventListener("nw:unread-mentions", onMentions);
  }, []);

  useEffect(() => {
    // Live count arrives via useSitePresence WS events
    const onCount = (e) => {
      const n = e?.detail?.count;
      if (typeof n === "number") setOnline(n);
    };
    window.addEventListener("nw:online-count", onCount);
    // Fallback seed from REST so we show a number immediately even before WS opens
    if (user) {
      api.get("/stats/pulse").then(({ data }) => {
        setOnline((prev) => (prev == null ? (data?.live_now ?? 0) : prev));
      }).catch(() => {});
    }
    return () => window.removeEventListener("nw:online-count", onCount);
  }, [user]);

  return (
    <header className="border-b border-white/10 bg-obsidian sticky top-0 z-30" data-testid="topbar">
      <div className="flex items-center justify-between px-4 md:px-8 h-14">
        <Link to="/lobby" className="flex items-center gap-2" data-testid="brand">
          <Radio className="w-4 h-4 text-acid" />
          <span className="font-heading text-2xl leading-none">NITE<span className="text-acid">WIRE</span></span>
          <span className="hidden md:inline font-mono text-[10px] text-white/40 tracking-widest ml-2">// ALWAYS LIVE</span>
        </Link>
        <nav className="flex items-center gap-1 md:gap-2">
          {online !== null && (
            <div
              data-testid="online-count-chip"
              title={`${online} ${online === 1 ? "person" : "people"} online right now`}
              className="hidden sm:flex items-center gap-1 border border-acid/40 px-2 py-1 font-mono text-[10px] text-acid uppercase tracking-widest"
            >
              <span className="w-1.5 h-1.5 bg-acid animate-pulse" />
              <UsersIcon className="w-3 h-3" />
              <span data-testid="online-count-value">{online}</span>
              <span className="hidden md:inline text-white/50">online</span>
            </div>
          )}
          <Link to="/lobby" data-testid="nav-lobby" className="font-mono text-xs px-3 py-2 text-white/70 hover:text-acid uppercase tracking-widest">
            Lobby
          </Link>
          <Link to="/dm" data-testid="nav-dms" className="relative font-mono text-xs px-3 py-2 text-white/70 hover:text-acid uppercase tracking-widest flex items-center gap-1">
            <MessageSquare className="w-3 h-3" /> DMs
            {unread > 0 && (
              <span
                data-testid="dm-unread-badge"
                className="absolute -top-1 -right-0 min-w-[18px] h-[18px] px-1 bg-blaze text-obsidian font-mono text-[10px] font-bold flex items-center justify-center animate-pulse"
                aria-label={`${unread} unread DMs`}
              >
                {unread > 9 ? "9+" : unread}
              </span>
            )}
          </Link>
          <Link to="/mentions" data-testid="nav-mentions" className="relative font-mono text-xs px-3 py-2 text-white/70 hover:text-hotpink uppercase tracking-widest flex items-center gap-1">
            <AtSign className="w-3 h-3" /> <span className="hidden md:inline">Mentions</span>
            {mentionsUnread > 0 && (
              <span
                data-testid="mentions-unread-badge"
                className="absolute -top-1 -right-0 min-w-[18px] h-[18px] px-1 bg-hotpink text-obsidian font-mono text-[10px] font-bold flex items-center justify-center animate-pulse"
                aria-label={`${mentionsUnread} unread mentions`}
              >
                {mentionsUnread > 9 ? "9+" : mentionsUnread}
              </span>
            )}
          </Link>
          <Link to="/profile" data-testid="nav-profile" className="font-mono text-xs px-3 py-2 text-white/70 hover:text-acid uppercase tracking-widest flex items-center gap-1">
            <User className="w-3 h-3" /> {user?.username}
          </Link>
          {user?.is_admin && (
            <Link to="/admin" data-testid="nav-admin" className="font-mono text-xs px-3 py-2 text-hotpink hover:text-acid uppercase tracking-widest flex items-center gap-1" title="Admin dashboard">
              <Shield className="w-3 h-3" /> <span className="hidden md:inline">Admin</span>
            </Link>
          )}
          <button
            data-testid="btn-logout"
            onClick={() => {
              // Mobile users on the browser get the install nudge first. We
              // navigate BEFORE clearing the session so <Protected> on the
              // current route doesn't intercept and redirect to /enter.
              // The install screen itself does the actual sign-out on mount.
              if (isMobile() && !isStandalonePWA()) {
                nav("/install?logout=1", { replace: true });
              } else {
                logout();
                nav("/enter", { replace: true });
              }
            }}
            className="font-mono text-xs px-3 py-2 text-white/50 hover:text-blaze uppercase tracking-widest flex items-center gap-1"
          >
            <LogOut className="w-3 h-3" /> Out
          </button>
        </nav>
      </div>
    </header>
  );
}
